import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import styles from './Drawer.module.scss';

/* ────────────────────────────────────────────────────────────
 * Drawer — right-side slide-in panel.
 *
 * Use for filter UIs, detail peeks, side editors. For "modal
 * dialog" needs (confirmations, forms that block the page) use
 * <Modal>; for "supplementary side surface" needs use this.
 *
 *   <Drawer
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     title="Filter orders"
 *     size="md"
 *     footer={<Button onClick={apply}>Apply</Button>}
 *   >
 *     {children}
 *   </Drawer>
 *
 * Motion (MOTION.md §4.2):
 *   - Open: translateX(100%) → 0 over 280ms ease-in-out-cubic.
 *   - Close: reverse over 240ms (slightly faster — leaving < arriving).
 *   - Backdrop fades 0 → 1 over --transition-modal.
 * ──────────────────────────────────────────────────────────── */

const SIZE_MAP = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
};

const Drawer = ({
  open,
  onClose,
  title,
  size,
  footer,
  hideCloseButton,
  className,
  children,
}) => {
  const surfaceRef = useRef(null);

  // Body scroll lock while drawer is open (MOTION.md §4.2: lock snaps
  // instantly — do not animate the lock itself).
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Focus the drawer surface when it opens so screen readers announce
  // the new region and the Escape handler picks up keystrokes.
  useEffect(() => {
    if (!open) return undefined;
    const node = surfaceRef.current;
    if (node) {
      const focusable = node.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (focusable || node).focus();
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const surfaceClass = [styles.surface, SIZE_MAP[size] || styles.sizeMd, className || '']
    .filter(Boolean)
    .join(' ');

  const node = (
    <div className={styles.root} role="presentation">
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={surfaceRef}
        className={surfaceClass}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        {(title || !hideCloseButton) && (
          <header className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {!hideCloseButton && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close"
              >
                <X size={20} strokeWidth={1.75} />
              </button>
            )}
          </header>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </aside>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

Drawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  footer: PropTypes.node,
  hideCloseButton: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

Drawer.defaultProps = {
  open: false,
  title: '',
  size: 'md',
  footer: null,
  hideCloseButton: false,
  className: '',
  children: null,
};

export default Drawer;
